import React, { FunctionComponent, useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';
import { Esi } from '@empiriecom/module-components/Esi';
import { Modal } from '@empiriecom/module-components/Modal';
import { Link } from '@empiriecom/module-components/Link';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import useTracking from '@empiriecom/module-components/hooks/useTracking';
import { FragmentConfiguration } from '@/config';
import { DeliveryType } from '@/interfaces';
import useUrlParameters from '../hooks/useUrlParameters';

const messages = defineMessages({
  default: {
    defaultMessage: 'Service & Versandkosten',
    id: 'price.tax.hint.layer.headline.default',
  },
  l: {
    defaultMessage: 'Speditionsgebühr',
    id: 'price.tax.hint.layer.headline.l',
  },
  s: {
    defaultMessage: 'Speditionsgebühr',
    id: 'price.tax.hint.layer.headline.s',
  },
});

const StyledTaxHint = styled.div`
  color: #a0a0a0;
  font-size: 0.6875rem;
  margin-bottom: 0.25rem;
`;

const ModalLink = styled(Link)`
  text-decoration: underline;
  color: #a0a0a0;
  background: none;
  border: 0px none;
  box-shadow: 0px none;
  padding: 0;
  margin: 0;
  display: inline;
  font-size: inherit;
  &&:hover {
    color: #a0a0a0;
    border: 0px none;
  }
`;

const EsiContainer = styled.div`
  .modalbox-headline {
    display: none;
  }
`;

type TaxHintProps = {
  deliveryType?: DeliveryType;
  isModal?: boolean;
};

/**
 * This is the tax hint component. It renders the by law required tax hint. It also
 * renders a modal link (represented by the underlined part). If the link is clicked
 * a modal box opens containing further information on delivery and service charges.
 * <p>The text and modal content contain display logic to switch the content by a given deliveryType</p>
 */
export const TaxHint: FunctionComponent<TaxHintProps> = ({
  deliveryType = 'default',
  isModal = true,
}): JSX.Element => {
  const [modalOpen, setModalOpen] = useState(false);
  const { taxHint } = useConfig<FragmentConfiguration>();
  const intl = useIntl();
  const dispatchGTMEvent = useTracking();

  const modalLinkMessages = {
    default: (
      <FormattedMessage
        id="price.tax.hint.link.default"
        defaultMessage="Service- & Versandkosten"
      />
    ),
    l: <FormattedMessage id="price.tax.hint.link.l" defaultMessage="Speditionsgebühr" />,
    s: <FormattedMessage id="price.tax.hint.link.s" defaultMessage="Speditionsgebühr" />,
  };

  return (
    <>
      <StyledTaxHint data-testid="tax-hint">
        <FormattedMessage id="price.tax.hint.text" defaultMessage="inkl. gesetzl. MwSt, zzgl." />
        <span>&#160;</span>
        {taxHint.modalContent[deliveryType].useLink && isModal ? (
          <ModalLink
            data-testid="tax-hint-modal-link"
            onClick={() => {
              setModalOpen(true);
              dispatchGTMEvent({
                event: 'ga_event',
                eventAction: 'viewLayer',
                eventCategory: 'noteLayer',
                eventLabel: 'detailview',
                eventValue: intl
                  .formatMessage(messages[deliveryType])
                  .concat(' (Liefertyp: ', deliveryType, ')'),
              });
            }}
            renderAs="span"
            to=""
          >
            {modalLinkMessages[deliveryType]}
          </ModalLink>
        ) : (
          modalLinkMessages[deliveryType]
        )}
      </StyledTaxHint>
      {taxHint.modalContent[deliveryType].useLink && isModal && (
        <Modal
          isOpen={modalOpen}
          headline={intl.formatMessage(messages[deliveryType])}
          onRequestClose={() => setModalOpen(false)}
        >
          <EsiContainer data-testid="tax-hint-modal">
            {taxHint.useESI ? (
              <Esi
                src={useUrlParameters(taxHint.modalContent[deliveryType].link, undefined, true)}
              />
            ) : (
              <FormattedMessage
                id="price.tax.hint.modal"
                defaultMessage="Je Bestellung und Lieferanschrift zahlen Sie für Standardlieferungen 6 € Versandkosten (unabhängig vom Bestellwert)."
              />
            )}
          </EsiContainer>
        </Modal>
      )}
    </>
  );
};

export default TaxHint;
